import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProductSelection } from '@nucleus-commerce/api-client/admin';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { Label } from '@nucleus-commerce/ui/components/label';

import { ProductPicker } from '@/features/resource-pickers';

export interface AddMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scheduleId: string;
  onAdd: (picked: {
    selection: ProductSelection;
    titles: Map<string, string>;
    pinned: boolean;
  }) => void;
}

/**
 * The master catalog picker for the pool, with "Pin these" in its footer and the consequences
 * spelled out: drafts in every target store now, newcomers first in the next batch. The list
 * locks the pool's members and the products another schedule owns in a target store on every
 * page (`memberOf`), and "Select all N matching" sends the filter itself.
 */
export function AddMembersDialog({ open, onOpenChange, scheduleId, onAdd }: AddMembersDialogProps) {
  const { t } = useTranslation('schedules');
  const [pinned, setPinned] = useState(false);
  return (
    <ProductPicker
      scope={{ kind: 'org' }}
      open={open}
      onOpenChange={(next) => {
        if (!next) setPinned(false);
        onOpenChange(next);
      }}
      tour="schedules.detail.add-picker"
      title={t('pool.pickerTitle')}
      confirmLabel={t('pool.pickerConfirm')}
      memberOf={{ kind: 'schedule', id: scheduleId }}
      lockedLabel={t('pool.alreadyMember')}
      selectAllMatching
      footerContent={
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="schedule-add-pinned"
              checked={pinned}
              onCheckedChange={(checked) => setPinned(checked === true)}
            />
            <Label htmlFor="schedule-add-pinned" className="font-normal">
              {t('pool.addPinned')}
            </Label>
          </div>
          <p className="text-muted-foreground text-xs">{t('pool.addHint')}</p>
        </div>
      }
      onConfirm={(selection, productSelection) => {
        onAdd({
          selection: productSelection,
          titles: new Map(selection.items.map((p) => [p.id, p.title])),
          pinned,
        });
        setPinned(false);
      }}
    />
  );
}
